import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import qs from 'query-string';

import {
    RecommendationHistoryProps,
    RecommendationProps,
    RecommendationsProps,
} from './recommendations.interface';
import getEnvConfig from '../../../../../utils/config/env';
import { PaginatedResponse } from '../../../interfaces/PaginatedResponse';

export const recommendationsAIApi = createApi({
    reducerPath: 'recommendationsAi',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL,
        prepareHeaders: headers => {
            const token = localStorage.getItem('accessToken');
            headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['RecommendationsAI'],

    endpoints: builder => ({
        createRecommendations: builder.mutation<
            RecommendationProps[],
            RecommendationsProps
        >({
            query: payload => ({
                url: `/recommendations`,
                method: 'POST',
                body: payload,
            }),
            transformResponse: (response: { data: RecommendationProps[] }) => {
                return response.data;
            },
            invalidatesTags: ['RecommendationsAI'],
        }),

        readRecommendations: builder.query<
            PaginatedResponse<RecommendationProps>,
            { limit: number; offset: number; page?: number }
        >({
            query: ({ limit, offset, page }) => {
                const queryArgs = qs.stringify(
                    { limit, offset, page },
                    {
                        arrayFormat: 'bracket-separator',
                        skipNull: true,
                    }
                );
                return {
                    url: `/recommendations?${queryArgs}`,
                };
            },
            transformResponse: (
                response: { data: RecommendationProps[] },
                meta
            ) => {
                return {
                    items: response.data || [],
                    total:
                        Number(meta.response.headers.get('Items-Count')) || 0,
                };
            },
            providesTags: ['RecommendationsAI'],
        }),

        readRecommendation: builder.query<RecommendationHistoryProps[], number>(
            {
                query: id => {
                    return {
                        url: `/recommendations/${id}/history`,
                    };
                },
                transformResponse: (response: {
                    data: RecommendationHistoryProps[];
                }) => {
                    return response.data || [];
                },
            }
        ),

        deleteRecommendation: builder.mutation({
            query: id => ({
                url: `/recommendations/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['RecommendationsAI'],
        }),

        updateRecommendations: builder.mutation<
            RecommendationProps[],
            { id: number; payload: RecommendationsProps }
        >({
            query: payload => ({
                url: `/recommendations/${payload.id}`,
                method: 'PATCH',
                body: payload,
            }),
            transformResponse: (response: { data: RecommendationProps[] }) => {
                return response.data;
            },
            invalidatesTags: ['RecommendationsAI'],
        }),
    }),
});

export const {
    useCreateRecommendationsMutation,
    useLazyReadRecommendationsQuery,
    useLazyReadRecommendationQuery,
    useDeleteRecommendationMutation,
    useUpdateRecommendationsMutation,
} = recommendationsAIApi;
